import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, ClockIcon, UserIcon } from '@heroicons/react/24/outline';

const News = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All News' },
    { id: 'markets', name: 'Markets' },
    { id: 'crypto', name: 'Crypto' },
    { id: 'earnings', name: 'Earnings' },
    { id: 'economy', name: 'Economy' },
  ];

  const articles = [
    {
      id: 1,
      title: 'Federal Reserve Signals Potential Rate Cut in Q2 2024',
      excerpt: 'Latest FOMC meeting minutes suggest central bank may ease monetary policy as inflation concerns subside...',
      category: 'economy',
      author: 'Sarah Johnson',
      publishedAt: '2024-01-15T10:30:00Z',
      readTime: '3 min read',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=400&h=250&fit=crop',
      featured: true
    },
    {
      id: 2,
      title: 'Bitcoin Reaches New Monthly High as Institutional Adoption Grows',
      excerpt: 'Major corporations continue to add Bitcoin to their treasury reserves, driving price momentum...',
      category: 'crypto',
      author: 'Michael Chen',
      publishedAt: '2024-01-15T08:15:00Z',
      readTime: '2 min read',
      image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=250&fit=crop'
    },
    {
      id: 3,
      title: 'Tech Earnings Season: What to Expect from FAANG Stocks',
      excerpt: 'Analysts predict mixed results as AI investments weigh on short-term profitability...',
      category: 'earnings',
      author: 'Emily Rodriguez',
      publishedAt: '2024-01-14T16:45:00Z',
      readTime: '4 min read',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop'
    },
    {
      id: 4,
      title: 'Global Supply Chain Recovery Boosts Commodity Prices',
      excerpt: 'Improved logistics and reduced bottlenecks drive optimism in commodities markets...',
      category: 'markets',
      author: 'David Kim',
      publishedAt: '2024-01-14T14:20:00Z',
      readTime: '3 min read',
      image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&h=250&fit=crop'
    },
    {
      id: 5,
      title: 'European Markets Rally on Positive Economic Data',
      excerpt: 'Strong manufacturing PMI and consumer confidence data boost European equity markets...',
      category: 'markets',
      author: 'Anna Schmidt',
      publishedAt: '2024-01-14T11:10:00Z',
      readTime: '2 min read',
      image: 'https://images.unsplash.com/photo-1559589689-577aabd1db4f?w=400&h=250&fit=crop'
    }
  ];

  const filteredArticles = activeCategory === 'all' 
    ? articles 
    : articles.filter(article => article.category === activeCategory);

  const featuredArticle = articles.find(article => article.featured);
  const regularArticles = articles.filter(article => !article.featured);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Market News</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Stay informed with the latest market developments, analysis, and insights
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Article */}
        {featuredArticle && activeCategory === 'all' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="card overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    className="w-full h-64 md:h-full object-cover"
                  />
                </div>
                <div className="md:w-1/2 p-8">
                  <div className="flex items-center mb-4">
                    <span className="inline-block px-3 py-1 text-xs font-semibold bg-primary-100 text-primary-800 rounded-full">
                      Featured
                    </span>
                    <span className="mx-2 text-gray-300">•</span>
                    <span className="text-sm text-gray-500 capitalize">{featuredArticle.category}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6">
                    {featuredArticle.excerpt}
                  </p>
                  <div className="flex items-center text-sm text-gray-500">
                    <UserIcon className="h-4 w-4 mr-1" />
                    <span className="mr-4">{featuredArticle.author}</span>
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    <span className="mr-4">{formatDate(featuredArticle.publishedAt)}</span>
                    <ClockIcon className="h-4 w-4 mr-1" />
                    <span>{featuredArticle.readTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredArticles.map((article, index) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            >
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="flex items-center mb-3">
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded capitalize">
                    {article.category}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center text-sm text-gray-500">
                  <UserIcon className="h-4 w-4 mr-1" />
                  <span className="mr-3">{article.author}</span>
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  <span className="mr-3">{formatDate(article.publishedAt)}</span>
                  <ClockIcon className="h-4 w-4 mr-1" />
                  <span>{article.readTime}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Load More Button */}
        <div className="text-center mt-12">
          <button className="btn-primary">
            Load More Articles
          </button>
        </div>
      </div>
    </div>
  );
};

export default News; 